"use client"

import { useQuery, useInfiniteQuery } from "@tanstack/react-query"
import {
  dedupeGalleryPosts,
  getGalleryPostDedupeKey,
  getGalleryRowDedupeKey,
} from "@/shared/lib/gallery/dedupe"
import { dbGalleryPostToGalleryPost } from "@/shared/lib/supabase/mappers"
import { queryKeys } from "@/shared/lib/query-keys"
import type { GalleryPost } from "@/shared/lib/supabase/mappers"
import type { SupabaseClient } from "@supabase/supabase-js"
import { getOptionalSupabasePublishableKey } from "@/shared/lib/supabase/keys"

export { dedupeGalleryPosts, getGalleryPostDedupeKey }

type TabValue = "for-you" | "trending" | "new" | "images"

const PAGE_SIZE = 20
const LOCAL_GALLERY_FETCH_MULTIPLIER = 4
const LOCAL_GALLERY_ROW_WINDOW = PAGE_SIZE * LOCAL_GALLERY_FETCH_MULTIPLIER
const GALLERY_POST_SELECT =
  "id,user_id,type,media_url,thumbnail_url,caption,aspect_ratio,tags,likes_count,comments_count,views_count,status,character_id,generation_params,created_at,updated_at,profiles(username,avatar_url)"
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
const SUPABASE_PUBLISHABLE_KEY = getOptionalSupabasePublishableKey()

function getRegisteredSupabaseClient(): SupabaseClient {
  const client = (
    globalThis as typeof globalThis & {
      __pleasuraiSupabaseClient?: SupabaseClient
    }
  ).__pleasuraiSupabaseClient
  if (!client) {
    throw new Error("The authenticated Supabase client has not been initialized")
  }
  return client
}

async function fetchPublicSupabaseRest<T>(
  table: string,
  params: URLSearchParams
): Promise<T> {
  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    throw new Error("Public Supabase configuration is unavailable")
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`,
    {
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
      },
    }
  )
  if (!response.ok) {
    throw new Error(`Public Supabase request failed (${response.status})`)
  }
  return response.json() as Promise<T>
}

function createGalleryPostParams(): URLSearchParams {
  return new URLSearchParams({
    select: GALLERY_POST_SELECT,
    status: "eq.active",
  })
}

interface FeedOptions {
  tab: TabValue
  tag?: string | null
  preferences?: string[] | null
  userId?: string | null
}

export interface GalleryViewerContext {
  userId?: string | null
  preferences?: string[] | null
  isLoading?: boolean
}

export interface GalleryFeedPage {
  posts: GalleryPost[]
  nextGalleryPageParam: GalleryPageParam | null
}

interface GalleryPageParam {
  rowOffset: number
  seenBatchKeys: string[]
}

const DETAIL_CACHE_PREFIX = "pleasurai:gallery-post:"
const INITIAL_GALLERY_PAGE_PARAM: GalleryPageParam = {
  rowOffset: 0,
  seenBatchKeys: [],
}
type GalleryPostDbRow = Parameters<typeof dbGalleryPostToGalleryPost>[0]

export function isExternalGalleryPostId(id: string): boolean {
  return id.startsWith("community-") || id.startsWith("civitai-")
}

function stripViewerStateFromGalleryPost(post: GalleryPost): GalleryPost {
  const sanitizedPost: GalleryPost = {
    ...post,
    batchImages: post.batchImages?.map(stripViewerStateFromGalleryPost),
  }
  delete sanitizedPost.isLiked
  return sanitizedPost
}

export function cacheGalleryPostForDetail(post: GalleryPost) {
  if (typeof window === "undefined") return

  try {
    window.sessionStorage.setItem(
      `${DETAIL_CACHE_PREFIX}${post.id}`,
      JSON.stringify(stripViewerStateFromGalleryPost(post))
    )
  } catch {
    // Session storage is an enhancement only. Network fetch remains the fallback.
  }
}

function readCachedGalleryPost(id: string): GalleryPost | null {
  if (typeof window === "undefined") return null

  try {
    const raw = window.sessionStorage.getItem(`${DETAIL_CACHE_PREFIX}${id}`)
    if (!raw) return null
    return JSON.parse(raw) as GalleryPost
  } catch {
    return null
  }
}

export function getGalleryBatchKey(post: GalleryPost): string | null {
  const generationId = post.generationParams?.generation_id?.trim()
  return generationId ? `generation:${generationId}` : null
}

function getGalleryRowGenerationId(row: GalleryPostDbRow): string | null {
  const params = row.generation_params as { generation_id?: unknown } | null
  const generationId =
    typeof params?.generation_id === "string" ? params.generation_id.trim() : ""

  return generationId || null
}

function toBatchImage(post: GalleryPost): GalleryPost {
  return {
    id: post.id,
    userId: post.userId,
    username: post.username,
    userAvatar: post.userAvatar,
    type: post.type,
    mediaUrl: post.mediaUrl,
    thumbnailUrl: post.thumbnailUrl,
    aspectRatio: post.aspectRatio,
    caption: post.caption,
    likes: post.likes,
    comments: post.comments,
    tags: post.tags,
    createdAt: post.createdAt,
    isLiked: post.isLiked,
    generationParams: post.generationParams,
  }
}

export function collapseGalleryBatches(posts: GalleryPost[]): GalleryPost[] {
  const collapsed: GalleryPost[] = []
  const batchIndexes = new Map<string, number>()
  const standaloneIds = new Set<string>()

  posts.forEach((post) => {
    const batchKey = getGalleryBatchKey(post)

    if (!batchKey) {
      if (standaloneIds.has(post.id)) return
      standaloneIds.add(post.id)
      collapsed.push(post)
      return
    }

    const existingIndex = batchIndexes.get(batchKey)

    if (existingIndex === undefined) {
      batchIndexes.set(batchKey, collapsed.length)
      collapsed.push({ ...post, batchCount: 1, batchImages: [toBatchImage(post)] })
      return
    }

    const existing = collapsed[existingIndex]
    const existingImages = existing.batchImages ?? [toBatchImage(existing)]
    const hasImage = existingImages.some((image) => image.id === post.id)
    const batchImages = hasImage
      ? existingImages
      : [...existingImages, toBatchImage(post)]

    collapsed[existingIndex] = {
      ...existing,
      batchCount: batchImages.length,
      batchImages,
      likes: Math.max(existing.likes, post.likes),
    }
  })

  return collapsed
}

function buildGalleryParams(opts: FeedOptions): URLSearchParams {
  const { tab, tag } = opts
  const params = createGalleryPostParams()

  if (tag) {
    params.set("tags", `cs.{${tag}}`)
  }

  switch (tab) {
    case "trending":
      params.set("order", "likes_count.desc")
      break
    case "new":
      params.set("order", "created_at.desc")
      break
    case "images":
      params.set("type", "eq.image")
      params.set("order", "created_at.desc")
      break
    case "for-you":
    default:
      params.set("order", "likes_count.desc,created_at.desc")
      break
  }

  return params
}

async function fetchGalleryRows(
  opts: FeedOptions,
  rowOffset: number,
  rowLimit: number
): Promise<GalleryPostDbRow[]> {
  const params = buildGalleryParams(opts)
  params.set("offset", String(rowOffset))
  params.set("limit", String(rowLimit))
  return fetchPublicSupabaseRest<GalleryPostDbRow[]>("gallery_posts", params)
}

function formatPostgrestInList(values: string[]): string {
  return `(${values.map((value) => `"${value.replace(/["\\]/g, "\\$&")}"`).join(",")})`
}

async function fetchGalleryRowsForGenerationIds(
  generationIds: string[]
): Promise<GalleryPostDbRow[]> {
  if (generationIds.length === 0) return []

  const params = createGalleryPostParams()
  params.set(
    "generation_params->>generation_id",
    `in.${formatPostgrestInList(generationIds)}`
  )
  params.set("order", "created_at.asc")
  return fetchPublicSupabaseRest<GalleryPostDbRow[]>("gallery_posts", params)
}

async function expandGalleryRowsWithSiblings(
  leadRows: GalleryPostDbRow[]
): Promise<GalleryPostDbRow[]> {
  const generationIds = [
    ...new Set(leadRows.map(getGalleryRowGenerationId).filter(Boolean)),
  ] as string[]

  const siblingRows = await fetchGalleryRowsForGenerationIds(generationIds)
  const siblingRowsByGenerationId = new Map<string, GalleryPostDbRow[]>()

  for (const row of siblingRows) {
    const generationId = getGalleryRowGenerationId(row)
    if (!generationId) continue

    const rows = siblingRowsByGenerationId.get(generationId) ?? []
    rows.push(row)
    siblingRowsByGenerationId.set(generationId, rows)
  }

  return leadRows.flatMap((leadRow) => {
    const generationId = getGalleryRowGenerationId(leadRow)
    if (!generationId) return [leadRow]

    const rows = siblingRowsByGenerationId.get(generationId) ?? []
    const seenIds = new Set<string>()
    return [leadRow, ...rows].filter((row) => {
      if (seenIds.has(row.id)) return false
      seenIds.add(row.id)
      return true
    })
  })
}

async function fetchGalleryPosts(
  opts: FeedOptions,
  pageParam: GalleryPageParam
): Promise<GalleryFeedPage> {
  const { userId } = opts
  const pageRows: GalleryPostDbRow[] = []
  const seenBatchKeys = new Set(pageParam.seenBatchKeys)
  const pageBatchKeys = new Set<string>()
  let batchCount = 0
  let nextGalleryPageParam: GalleryPageParam | null = null
  let currentOffset = pageParam.rowOffset

  while (batchCount < PAGE_SIZE || nextGalleryPageParam === null) {
    const rows = await fetchGalleryRows(
      opts,
      currentOffset,
      LOCAL_GALLERY_ROW_WINDOW
    )

    if (rows.length === 0) break

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index]
      const pageKey = getGalleryRowDedupeKey(row)

      if (seenBatchKeys.has(pageKey) || pageBatchKeys.has(pageKey)) {
        continue
      }

      if (batchCount >= PAGE_SIZE) {
        nextGalleryPageParam = {
          rowOffset: currentOffset + index,
          seenBatchKeys: [...seenBatchKeys, ...pageBatchKeys],
        }
        break
      }

      pageBatchKeys.add(pageKey)
      batchCount++
      pageRows.push(row)
    }

    if (nextGalleryPageParam !== null) break

    currentOffset += rows.length

    if (rows.length < LOCAL_GALLERY_ROW_WINDOW) {
      nextGalleryPageParam = null
      break
    }
  }

  const expandedRows = await expandGalleryRowsWithSiblings(pageRows)

  // Check which posts the user has liked
  let likedPostIds = new Set<string>()
  if (userId && expandedRows.length > 0) {
    const supabase = getRegisteredSupabaseClient()
    const postIds = expandedRows.map((post) => post.id)
    const { data: likes } = await supabase
      .from("gallery_likes")
      .select("post_id")
      .eq("user_id", userId)
      .in("post_id", postIds)

    if (likes) {
      likedPostIds = new Set(likes.map((l: { post_id: string }) => l.post_id))
    }
  }

  const mappedPosts = expandedRows.map((row) =>
    dbGalleryPostToGalleryPost(row, likedPostIds.has(row.id))
  )

  return {
    posts: dedupeGalleryPosts(collapseGalleryBatches(mappedPosts)),
    nextGalleryPageParam,
  }
}

async function fetchCivitaiPost(id: string): Promise<GalleryPost | null> {
  const cached = readCachedGalleryPost(id)
  if (cached) return cached

  const params = new URLSearchParams({
    id,
    limit: "1",
  })

  const response = await fetch(`/api/explore/civitai?${params.toString()}`, {
    cache: "no-store",
  })
  if (!response.ok) return null

  const payload = (await response.json()) as { posts?: GalleryPost[] }
  return payload.posts?.[0] ?? null
}

async function fetchRelatedFeaturedPosts(
  postId: string,
  limit: number
): Promise<GalleryPost[]> {
  const params = new URLSearchParams({
    limit: String(limit + 4),
    tag: "featured",
    sync: "false",
  })

  const response = await fetch(`/api/explore/featured?${params.toString()}`, {
    cache: "no-store",
  })
  if (!response.ok) return []

  const payload = (await response.json()) as { posts?: GalleryPost[] }
  const posts = dedupeGalleryPosts(payload.posts ?? [])
    .filter((post) => post.id !== postId)
    .slice(0, limit)
  posts.forEach(cacheGalleryPostForDetail)
  return posts
}

async function fetchExplorePosts(
  opts: FeedOptions,
  pageParam: GalleryPageParam
): Promise<GalleryFeedPage> {
  const page = await fetchGalleryPosts(opts, pageParam)
  return {
    ...page,
    posts: dedupeGalleryPosts(page.posts),
  }
}

export function useGalleryPosts(
  tab: TabValue,
  tag?: string | null,
  viewer: GalleryViewerContext = {}
) {
  const preferences = viewer.preferences ?? null
  const userId = viewer.userId ?? null
  const preferencesKey = preferences?.join("|") ?? null

  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.gallery.feed(tab),
      "platform-native-v4",
      tag ?? null,
      preferencesKey,
      userId ?? null,
    ],
    queryFn: ({ pageParam }) =>
      fetchExplorePosts({ tab, tag, preferences, userId }, pageParam),
    enabled: viewer.isLoading !== true,
    initialPageParam: INITIAL_GALLERY_PAGE_PARAM,
    getNextPageParam: (lastPage) => lastPage.nextGalleryPageParam ?? undefined,
  })
}

async function fetchGalleryPost(
  id: string,
  userId?: string | null
): Promise<GalleryPost | null> {
  if (isExternalGalleryPostId(id)) {
    return fetchCivitaiPost(id)
  }

  const selectedParams = createGalleryPostParams()
  selectedParams.set("id", `eq.${id}`)
  selectedParams.set("limit", "1")
  const data = await fetchPublicSupabaseRest<GalleryPostDbRow[]>(
    "gallery_posts",
    selectedParams
  )
  if (!data[0]) return null

  const selectedRow = data[0]
  const selectedPost = dbGalleryPostToGalleryPost(selectedRow)
  const generationId = selectedPost.generationParams?.generation_id?.trim()
  let rows: GalleryPostDbRow[] = [selectedRow]

  if (generationId) {
    const siblingParams = createGalleryPostParams()
    siblingParams.set("generation_params->>generation_id", `eq.${generationId}`)
    siblingParams.set("order", "created_at.asc")
    const siblingRows = await fetchPublicSupabaseRest<GalleryPostDbRow[]>(
      "gallery_posts",
      siblingParams
    )

    if (siblingRows.length > 0) {
      rows = siblingRows.sort((a, b) => {
        if (a.id === id) return -1
        if (b.id === id) return 1
        return 0
      })
    }
  }

  let likedPostIds = new Set<string>()
  if (userId && rows.length > 0) {
    const supabase = getRegisteredSupabaseClient()
    const { data: likes } = await supabase
      .from("gallery_likes")
      .select("post_id")
      .eq("user_id", userId)
      .in("post_id", rows.map((row) => row.id))

    if (likes) {
      likedPostIds = new Set(likes.map((like: { post_id: string }) => like.post_id))
    }
  }

  const mappedPosts = rows.map((row) =>
    dbGalleryPostToGalleryPost(row, likedPostIds.has(row.id))
  )

  return collapseGalleryBatches(mappedPosts)[0] ?? null
}

export function useGalleryPost(
  id: string | null,
  viewer: GalleryViewerContext = {}
) {
  return useQuery({
    queryKey: [...queryKeys.gallery.post(id ?? ""), viewer.userId ?? null],
    queryFn: () => fetchGalleryPost(id!, viewer.userId),
    enabled: !!id && viewer.isLoading !== true,
    initialData: () => (id ? readCachedGalleryPost(id) ?? undefined : undefined),
  })
}

// ── Related Posts ──────────────────────────────────────────────────────────

async function fetchRelatedPosts(
  postId: string,
  tags: string[],
  limit: number,
  userId?: string | null
): Promise<GalleryPost[]> {
  if (isExternalGalleryPostId(postId)) {
    return fetchRelatedFeaturedPosts(postId, limit)
  }

  // Try to find posts matching any of the same tags
  const relatedParams = createGalleryPostParams()
  relatedParams.set("id", `neq.${postId}`)
  relatedParams.set("order", "likes_count.desc")
  relatedParams.set("limit", String(limit))

  if (tags.length > 0) {
    relatedParams.set("tags", `ov.{${tags.join(",")}}`)
  }

  // If not enough results from tags, fetch trending as fallback
  let results = await fetchPublicSupabaseRest<GalleryPostDbRow[]>(
    "gallery_posts",
    relatedParams
  )
  if (results.length < limit && tags.length > 0) {
    const existingIds = new Set(results.map((r: { id: string }) => r.id))
    existingIds.add(postId)

    const fallbackParams = createGalleryPostParams()
    fallbackParams.set(
      "id",
      `not.in.(${Array.from(existingIds).join(",")})`
    )
    fallbackParams.set("order", "likes_count.desc")
    fallbackParams.set("limit", String(limit - results.length))
    const fallback = await fetchPublicSupabaseRest<GalleryPostDbRow[]>(
      "gallery_posts",
      fallbackParams
    )
    results = [...results, ...fallback]
  }

  // Check likes
  let likedPostIds = new Set<string>()
  if (userId && results.length > 0) {
    const supabase = getRegisteredSupabaseClient()
    const postIds = results.map((p: { id: string }) => p.id)
    const { data: likes } = await supabase
      .from("gallery_likes")
      .select("post_id")
      .eq("user_id", userId)
      .in("post_id", postIds)

    if (likes) {
      likedPostIds = new Set(likes.map((l: { post_id: string }) => l.post_id))
    }
  }

  const mappedPosts = results.map((row: GalleryPostDbRow) =>
    dbGalleryPostToGalleryPost(row, likedPostIds.has(row.id))
  )

  return collapseGalleryBatches(mappedPosts).slice(0, limit)
}

export function useRelatedPosts(
  postId: string,
  tags: string[],
  limit = 12,
  viewer: GalleryViewerContext = {}
) {
  return useQuery({
    queryKey: [
      ...queryKeys.gallery.related(postId),
      tags.join("|"),
      limit,
      viewer.userId ?? null,
    ],
    queryFn: () => fetchRelatedPosts(postId, tags, limit, viewer.userId),
    enabled: !!postId && viewer.isLoading !== true,
  })
}
